import type { ComicAnalysis } from "@/api/client";
import { Users, Palette, Layout, BookOpen } from "lucide-react";

interface ComicAnalysisDisplayProps {
  analysis: ComicAnalysis;
}

export function ComicAnalysisDisplay({ analysis }: ComicAnalysisDisplayProps) {
  const { storyAnalysis, panels, style } = analysis;

  return (
    <div className="space-y-6">
      {/* Story Analysis */}
      <section className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-purple-500" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">故事分析</h2>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">类型</p>
            <p className="font-medium text-foreground">{storyAnalysis.genre}</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">情绪</p>
            <p className="font-medium text-foreground">{storyAnalysis.tone}</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">时代</p>
            <p className="font-medium text-foreground">{storyAnalysis.era}</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">主题</p>
            <p className="font-medium text-foreground">{storyAnalysis.themes.join(', ')}</p>
          </div>
        </div>

        {/* Characters */}
        {storyAnalysis.characters.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">角色设定</p>
            </div>
            <div className="grid gap-3">
              {storyAnalysis.characters.map((char, index) => (
                <div key={index} className="bg-muted/30 rounded-lg p-3 border border-border/50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-foreground">{char.name}</span>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                      {char.personality}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{char.appearance}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Art Style */}
      <section className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-pink-500/10 flex items-center justify-center">
            <Palette className="w-5 h-5 text-pink-500" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">艺术风格</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">风格</p>
            <p className="font-medium text-foreground">{style.artStyle}</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">线条</p>
            <p className="font-medium text-foreground text-sm">{style.lineStyle}</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">色彩</p>
            <p className="font-medium text-foreground text-sm">{style.colorPalette}</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">光影</p>
            <p className="font-medium text-foreground text-sm">{style.lighting}</p>
          </div>
        </div>
      </section>

      {/* Panels Plan */}
      <section className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Layout className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">分镜设计</h2>
            <p className="text-sm text-muted-foreground">{panels.length} 格漫画</p>
          </div>
        </div>

        <div className="grid gap-3">
          {panels.map((panel) => (
            <div 
              key={panel.panelNumber} 
              className="bg-muted/30 rounded-lg p-4 border border-border/50"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary">{panel.panelNumber}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground mb-1">{panel.narrativeGoal}</p>
                  <p className="text-sm text-muted-foreground mb-2">{panel.sceneDescription}</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="bg-blue-500/10 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded">
                      {panel.camera}
                    </span>
                    <span className="bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded">
                      {panel.emotion}
                    </span>
                    {panel.characters.map((char, i) => (
                      <span key={i} className="bg-green-500/10 text-green-600 dark:text-green-400 px-2 py-0.5 rounded">
                        {char}
                      </span>
                    ))}
                  </div>
                  {panel.dialogue && (
                    <div className="mt-2 bg-background rounded p-2 border border-border/50">
                      <p className="text-sm italic text-foreground">"{panel.dialogue}"</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
